/**
 * Powtarzane slowa: ile razy kazdy mowca uzyl slow i zwrotow z listy methodology/powtarzane-slowa.json, w ktorych
 * wypowiedziach i w jakim otoczeniu.
 *
 * Skad to jest: autor metodyki poprosil w recenzji, zeby sprawdzic, jak czesto i w jakich kontekstach mowca uzywa
 * danego slowa ("Wtedy bedzie mozna postawic dalsze hipotezy"). Hipotez o osobie (np. o wyksztalceniu), ktore
 * podsunal w tym wpisie, funkcja swiadomie nie wspiera: liczy tylko, jak czesto i w jakim otoczeniu slowo pada.
 * Liczymy to DETERMINISTYCZNIE, bez modelu i bez kosztu.
 *
 * TO JEST LICZENIE, NIE OCENA. Wynik mowi "slowo X padlo 5 razy, w tych wypowiedziach, w takim otoczeniu" i nic
 * wiecej: nie mowi, czy to dobrze, ani niczego o czlowieku. Wnioski (i hipotezy do sprawdzenia) stawia czlowiek.
 *
 * Zasady liczenia:
 *  - cale slowa, bez wzgledu na wielkosc liter; granice slow wyznaczaja litery, cyfry i znaki laczace (\p{L}, \p{N},
 *    \p{M}), wiec polskie znaki sa czescia slowa, a "dobra" nie liczy sie w "dobrany" ani w odmianie z ogonkiem;
 *  - formy sa wpisane jawnie na liscie (bez odmiany i bez rdzeni): liczymy dokladnie to, co tam stoi;
 *  - zwrot wielowyrazowy ("w sumie") pasuje tylko wtedy, gdy miedzy jego slowami sa same biale znaki (dowolnie wiele):
 *    "tak, naprawde" to nie "tak naprawde";
 *  - gdy zwrot i slowo z listy nachodza na siebie, wygrywa najdluzsze dopasowanie w danym miejscu, a slowa, ktore
 *    juz policzono w zwrocie, nie licza sie drugi raz;
 *  - tekst przechodzi normalizacje NFC, wiec litera zapisana jako znak bazowy z akcentem laczacym liczy sie tak samo
 *    jak litera gotowa.
 *
 * Czyste funkcje: zero DOM, zero API Node. Liste podaje wolajacy (klient importuje JSON przy budowaniu, serwer go
 * waliduje przy starcie), wiec ten plik nie zalezy od zadnego pliku z dysku.
 */

/** Pozycja listy: slowo albo zwrot pokazywany w interfejsie i formy, ktore liczymy. */
export interface PozycjaListySlow {
  /** Np. "w sumie". Pokazywane w interfejsie; jest tez jedna z `formy`. */
  slowo: string;
  /** Formy liczone jako to slowo, wpisane jawnie, np. ["okej", "ok"]. */
  formy: readonly string[];
}

/** Wypowiedz w postaci, jaka zna ta funkcja. Kazdy widok mapuje na nia swoje dane. */
export interface WypowiedzDoLiczenia {
  /** Klucz mowcy, porownywany wylacznie na rownosc (Speaker.id albo indeks z listy mowcow linku recenzenta). */
  mowca: string;
  /** Numer wypowiedzi od 1, ten sam co w transkrypcie (shared/numeracja.ts). */
  numer: number;
  tekst: string;
}

/**
 * Zakres znakow trafienia w tekscie wypowiedzi: od `start` wlacznie do `koniec` wylacznie. Liczony na tekscie po
 * normalizacji NFC, wiec dla tekstu zapisanego w NFC (zwykly przypadek) to pozycja w oryginale. Transkrypt zaznacza
 * po nim trafienie po przejsciu z listy i sam sprawdza, czy tekst jest w NFC (inaczej nie zaznacza niczego).
 */
export interface ZakresTrafienia {
  start: number;
  koniec: number;
}

/** Jedno wystapienie z krotkim kontekstem: kilka slow przed i po, tak jak stoja w tekscie (z interpunkcja). */
export interface WystapienieSlowa extends ZakresTrafienia {
  /** Numer wypowiedzi od 1. */
  numer: number;
  /** Tekst przed trafieniem; konczy sie spacja, jesli w tekscie byla. Pusty na poczatku wypowiedzi. */
  przed: string;
  /** Trafienie tak, jak stoi w tekscie (wielkosc liter z tekstu, biale znaki zwiniete do jednej spacji). */
  trafienie: string;
  /** Tekst po trafieniu; zaczyna sie spacja albo interpunkcja z tekstu. */
  po: string;
  /** W wypowiedzi bylo cos jeszcze przed `przed` (interfejs stawia wtedy trzy kropki). */
  ucietyPoczatek: boolean;
  /** W wypowiedzi bylo cos jeszcze po `po`. */
  ucietyKoniec: boolean;
}

export interface PowtarzaneSlowo {
  /** Etykieta z listy (PozycjaListySlow.slowo). */
  slowo: string;
  /** Liczba wystapien u tego mowcy. */
  liczba: number;
  /** Numery wypowiedzi z tym slowem, bez powtorzen, rosnaco. */
  numery: number[];
  /** Wszystkie wystapienia w kolejnosci rozmowy. */
  wystapienia: WystapienieSlowa[];
}

/** Od ilu wystapien slowo trafia do wyniku. Jedno uzycie to nie powtorzenie. */
export const MINIMUM_POWTORZEN = 2;

/** Ile slow kontekstu pokazujemy przed trafieniem i po nim. */
export const SLOWA_KONTEKSTU = 4;

export interface OpcjeLiczenia {
  /** Domyslnie MINIMUM_POWTORZEN. Wartosci ponizej 1 dzialaja jak 1. */
  minimum?: number;
  /** Domyslnie SLOWA_KONTEKSTU. Wartosci ujemne dzialaja jak 0. */
  slowaKontekstu?: number;
}

interface Token {
  /** Slowo malymi literami: do porownan. */
  slowo: string;
  start: number;
  koniec: number;
}

/** Ciag liter, cyfr i znakow laczacych, czyli jedno slowo. Flaga u: \p{...} i pary zastepcze. */
const SLOWO = /[\p{L}\p{N}\p{M}]+/gu;
const TYLKO_BIALE = /^\s+$/u;
const BIALE = /\s+/gu;

function tokeny(tekst: string): Token[] {
  const wynik: Token[] = [];
  for (const m of tekst.matchAll(SLOWO)) {
    const start = m.index ?? 0;
    wynik.push({ slowo: m[0].toLowerCase(), start, koniec: start + m[0].length });
  }
  return wynik;
}

/** Wzorzec do szukania: slowa formy malymi literami i pozycja na liscie (do kolejnosci i do etykiety). */
interface Wzorzec {
  slowa: string[];
  pozycja: number;
}

/**
 * Wzorce pogrupowane po pierwszym slowie, w kazdej grupie od najdluzszego (najdluzsze dopasowanie wygrywa), a przy
 * rownej dlugosci w kolejnosci listy. Forma, ktora po zamianie na slowa jest pusta (sama interpunkcja), nie liczy sie.
 * Ta sama forma na dwoch pozycjach liczy sie przy pierwszej z nich (plik z metodyki tego nie dopuszcza, ale funkcja
 * ma byc przewidywalna dla kazdej listy).
 */
function przygotujWzorce(lista: readonly PozycjaListySlow[]): Map<string, Wzorzec[]> {
  const widziane = new Set<string>();
  const wgPierwszego = new Map<string, Wzorzec[]>();
  lista.forEach((pozycja, i) => {
    for (const forma of pozycja.formy) {
      const slowa = tokeny(forma.normalize("NFC")).map((t) => t.slowo);
      if (slowa.length === 0) continue;
      const klucz = slowa.join(" ");
      if (widziane.has(klucz)) continue;
      widziane.add(klucz);
      const grupa = wgPierwszego.get(slowa[0]) ?? [];
      grupa.push({ slowa, pozycja: i });
      wgPierwszego.set(slowa[0], grupa);
    }
  });
  for (const grupa of wgPierwszego.values()) {
    grupa.sort((a, b) => b.slowa.length - a.slowa.length || a.pozycja - b.pozycja);
  }
  return wgPierwszego;
}

/** Czy wzorzec stoi w tekscie od tokenu `od`: te same slowa, a miedzy nimi wylacznie biale znaki. */
function pasuje(tekst: string, t: readonly Token[], od: number, wzorzec: Wzorzec): boolean {
  if (od + wzorzec.slowa.length > t.length) return false;
  for (let k = 0; k < wzorzec.slowa.length; k++) {
    if (t[od + k].slowo !== wzorzec.slowa[k]) return false;
    if (k > 0 && !TYLKO_BIALE.test(tekst.slice(t[od + k - 1].koniec, t[od + k].start))) return false;
  }
  return true;
}

/** Krotki kontekst trafienia obejmujacego tokeny od..doKonca (wlacznie). */
function kontekst(
  tekst: string,
  t: readonly Token[],
  od: number,
  doKonca: number,
  ileSlow: number,
): Omit<WystapienieSlowa, "numer"> {
  const pierwszy = Math.max(0, od - ileSlow);
  const ostatni = Math.min(t.length - 1, doKonca + ileSlow);
  const ucietyPoczatek = pierwszy > 0;
  const ucietyKoniec = ostatni < t.length - 1;
  // Bez uciecia kontekst siega do brzegu wypowiedzi, zeby nie zgubic interpunkcji na jej koncu (np. "dobra.").
  const poczatek = ucietyPoczatek ? t[pierwszy].start : 0;
  const koniec = ucietyKoniec ? t[ostatni].koniec : tekst.length;
  const zwin = (s: string): string => s.replace(BIALE, " ");
  return {
    start: t[od].start,
    koniec: t[doKonca].koniec,
    przed: zwin(tekst.slice(poczatek, t[od].start)).trimStart(),
    trafienie: zwin(tekst.slice(t[od].start, t[doKonca].koniec)),
    po: zwin(tekst.slice(t[doKonca].koniec, koniec)).trimEnd(),
    ucietyPoczatek,
    ucietyKoniec,
  };
}

/**
 * Dla kazdego mowcy: slowa z listy uzyte co najmniej `minimum` razy (domyslnie 2), od najczestszych, a przy rownej
 * liczbie w kolejnosci listy. Przy kazdym slowie liczba wystapien, numery wypowiedzi i krotki kontekst kazdego
 * wystapienia.
 *
 * Mowca, u ktorego nic sie nie powtarza (albo ktory nie ma zadnej wypowiedzi), NIE MA wpisu w wyniku: wolajacy
 * traktuje brak wpisu jak pusta liste i nie pokazuje sekcji. Pusty transkrypt albo pusta lista daja pusty wynik.
 * Wypowiedzi moga przyjsc w dowolnej kolejnosci: wystapienia sa ukladane po numerze wypowiedzi.
 */
export function policzPowtarzaneSlowa(
  wypowiedzi: readonly WypowiedzDoLiczenia[],
  lista: readonly PozycjaListySlow[],
  opcje: OpcjeLiczenia = {},
): Map<string, PowtarzaneSlowo[]> {
  const minimum = Math.max(1, opcje.minimum ?? MINIMUM_POWTORZEN);
  const ileSlow = Math.max(0, opcje.slowaKontekstu ?? SLOWA_KONTEKSTU);
  const wzorce = przygotujWzorce(lista);

  /** Mowca -> pozycja na liscie -> wystapienia. */
  const wgMowcy = new Map<string, Map<number, WystapienieSlowa[]>>();

  const uporzadkowane = [...wypowiedzi].sort((a, b) => a.numer - b.numer);
  for (const wypowiedz of uporzadkowane) {
    const tekst = wypowiedz.tekst.normalize("NFC");
    const t = tokeny(tekst);
    let i = 0;
    while (i < t.length) {
      const trafiony = (wzorce.get(t[i].slowo) ?? []).find((w) => pasuje(tekst, t, i, w));
      if (!trafiony) {
        i++;
        continue;
      }
      const doKonca = i + trafiony.slowa.length - 1;
      const mowcy = wgMowcy.get(wypowiedz.mowca) ?? new Map<number, WystapienieSlowa[]>();
      const wystapienia = mowcy.get(trafiony.pozycja) ?? [];
      wystapienia.push({ numer: wypowiedz.numer, ...kontekst(tekst, t, i, doKonca, ileSlow) });
      mowcy.set(trafiony.pozycja, wystapienia);
      wgMowcy.set(wypowiedz.mowca, mowcy);
      i = doKonca + 1;
    }
  }

  const wynik = new Map<string, PowtarzaneSlowo[]>();
  for (const [mowca, wgPozycji] of wgMowcy) {
    const slowa = [...wgPozycji]
      .filter(([, wystapienia]) => wystapienia.length >= minimum)
      .sort(([pozA, a], [pozB, b]) => b.length - a.length || pozA - pozB)
      .map(([pozycja, wystapienia]) => ({
        slowo: lista[pozycja].slowo,
        liczba: wystapienia.length,
        numery: [...new Set(wystapienia.map((w) => w.numer))],
        wystapienia,
      }));
    if (slowa.length > 0) wynik.set(mowca, slowa);
  }
  return wynik;
}

/** Wystapienia jednego slowa w jednej wypowiedzi. */
export interface WystapieniaWWypowiedzi {
  /** Numer wypowiedzi od 1. */
  numer: number;
  /** Co najmniej jedno, w kolejnosci w tekscie. */
  wystapienia: WystapienieSlowa[];
}

/**
 * Wystapienia slowa pogrupowane po wypowiedziach, w kolejnosci rozmowy: lista pokazuje jedna pozycje na wypowiedz
 * (przycisk "Wypowiedz N" i konteksty wszystkich trafien w niej), a nie ten sam numer kilka razy pod rzad.
 */
export function grupujPoWypowiedziach(wystapienia: readonly WystapienieSlowa[]): WystapieniaWWypowiedzi[] {
  const wynik: WystapieniaWWypowiedzi[] = [];
  for (const w of [...wystapienia].sort((a, b) => a.numer - b.numer || a.start - b.start)) {
    const ostatnia = wynik[wynik.length - 1];
    if (ostatnia && ostatnia.numer === w.numer) ostatnia.wystapienia.push(w);
    else wynik.push({ numer: w.numer, wystapienia: [w] });
  }
  return wynik;
}
